<template>
   <div id="page-container" class="bg-white q-pa-md">
      <div id="main-container">
         <h1 class="text-left text-h4">Project Settings</h1>
         <p id="project-id">
            <span>Project Id: </span>{{ projectStore.projectId }}
         </p>
         <QCard>
            <QTabs
               v-model="tab"
               dense
               class="text-grey"
               active-color="primary"
               indicator-color="primary"
               align="justify"
               narrow-indicator
               @update:model-value="handleTabChange"
            >
               <QTab name="settings" label="Settings" />
               <QTab name="install" label="Install" />
               <QTab name="tours" label="Tours" />
            </QTabs>

            <QSeparator />

            <QTabPanels v-model="tab" animated>
               <QTabPanel name="settings">
                  <QForm
                     id="project-settings-edit-form"
                     @submit="openSaveDialog"
                  >
                     <div id="holdback-row">
                        <QField
                           id="holdback-field"
                           v-model="holdback"
                           label="Holdback Percentage"
                           borderless
                           hint="The percentage of sessions that will receive all default values."
                        >
                           <QSlider
                              id="holdback-slider"
                              v-model="holdback"
                              :min="0"
                              :max="100"
                              :step="5"
                              snap
                              label
                              :label-value="holdback + '%'"
                              label-always
                              markers
                              title="Holdback Percentage"
                              color="primary"
                           />
                        </QField>
                     </div>
                     <h5 class="text-left q-ma-none q-mt-md">
                        Optimization Metric
                     </h5>
                     <div id="optimization-metric-dropdown">
                        <QSelect
                           v-model="selectedStrategy"
                           :options="optimizationMetricOptions"
                           disable
                           label="Optimization Goal"
                           hint="Optimize for sessions with at least one reward event"
                           required
                        ></QSelect>
                     </div>
                     <div id="action-row" class="row justify-end">
                        <Protect :role="'org:_admin'">
                           <QBtn
                              id="deleteProject"
                              color="red"
                              class="action-button"
                              @click="onDeleteProject"
                              >Delete</QBtn
                           >
                           <QBtn
                              id="renameProject"
                              color="info"
                              class="action-button"
                              @click="onRenameProject"
                              >Rename</QBtn
                           >
                        </Protect>
                        <QBtn
                           id="submit"
                           type="submit"
                           color="primary"
                           class="action-button"
                           :disable="showDisabledSubmit"
                           >Update</QBtn
                        >
                     </div>
                  </QForm>
                  <div id="after-form">
                     <RouterLink id="org-home-link" :to="{ name: 'OrgHome' }"
                        >Looking for organization settings?</RouterLink
                     >
                  </div>
               </QTabPanel>
               <QTabPanel id="install-panel" name="install">
                  <CodeSnippets :header="false" :flat="true" />
               </QTabPanel>
               <QTabPanel id="tours-panel" name="tours">
                  <TourSettings />
               </QTabPanel>
            </QTabPanels>
         </QCard>
      </div>
   </div>
</template>
<script setup lang="ts">
import { QForm, QBtn, QSlider, useQuasar, QField } from "quasar"
import { ref, computed, Ref, onMounted, nextTick, watch } from "vue"
import { useProjectStore } from "../stores/projectStore"
import { useRouter, useRoute } from "vue-router"
import { toast } from "vue3-toastify"
import { OptimizationMetric } from "../types"
import { getOrg } from "@/services/clerk"
import { CodeSnippets } from "@components/index"
import { Protect } from "@clerk/vue"
import { callWithLoading } from "@utils/callWithLoading"
import { TourSettings } from "@/components/project-settings"
import { useTour } from "@/composables/useTour"
import { projectSettingsTour } from "@/tours/projectSettingsTour"

type SelectOption = {
   label: string
   value: string
   description: string
}
const $q = useQuasar()
const projectStore = useProjectStore()
const router = useRouter()
const route = useRoute()

const localProjectSettings = await callWithLoading(
   projectStore.getProjectSettings,
   $q,
   projectStore.projectId!,
)
const tab = ref<string>((route.meta.tab as string) || "settings")
// Watch for route changes to update the tab
watch(() => route.meta.tab, (newTab) => {
   if (newTab) {
      tab.value = newTab as string
   }
}, { immediate: true })
const holdback = ref<number>(localProjectSettings.settings.holdbackPercentage)
const defaultOptimizationMetric = {
   label: "Binary Reward",
   value: "binaryReward",
   description: "Maximize for sessions with at least one reward event",
}
const optimizationMetricOptions: Array<SelectOption> = [
   defaultOptimizationMetric,
   {
      label: "Count of Reward Events",
      value: "rewardCount",
      description:
         "Maximize the number of reward events that occurred in the session",
   },
   {
      label: "Reward Total",
      value: "totalReward",
      description:
         "Maximize total of all rewards across all reward events in a session",
   },
   // {
   //    label: "Time spent on page",
   //    value: "totalEngagement",
   //    description: "the total time spend on the page per session",
   // },
]

const startingStrategy = () => {
   // Since we only support Binary Reward right now, always
   // set the starting strategy to that

   return defaultOptimizationMetric

   // This would be the code to use if we supported multiple optimization metrics
   // if (!localProjectSettings.settings.optimizationMetric) {
   //    return defaultOptimizationMetric
   // }
   // return defaultOptimizationMetric
   // const match = _.find(optimizationMetricOptions, {
   //    value: localProjectSettings.settings.optimizationMetric.strategy,
   // })
   // if (match) {
   //    return match
   // } else {
   //    return defaultOptimizationMetric
   // }
}
const selectedStrategy = ref<SelectOption>(startingStrategy())
const strategyKey = computed(() => {
   if (selectedStrategy.value != null) {
      return selectedStrategy.value.value
   } else {
      return defaultOptimizationMetric.value
   }
})
const optimizationMetric: Ref<OptimizationMetric> = computed(() => {
   return {
      strategy: strategyKey.value,
   }
})

const openSaveDialog = () => {
   $q.dialog({
      title: "Save Project Settings",
      message: "Are you sure you want to save these project settings?",
      cancel: true,
   }).onOk(async () => {
      await callWithLoading(projectStore.updateProjectSettings, $q, {
         settings: {
            holdbackPercentage: holdback.value,
            optimizationMetric: optimizationMetric.value,
            rewards: localProjectSettings.settings.rewards,
         },
      })
      router.push({ name: "ProjectSettings" }).then(() => {
         toast.success("Project Settings updated")
      })
   })
}

const onDeleteProject = () => {
   $q.dialog({
      title: "Delete Project",
      message:
         "Are you sure you want to delete this project? This cannot be undone.",
      cancel: true,
   }).onOk(async () => {
      await callWithLoading(
         projectStore.deleteProject,
         $q,
         projectStore.projectId!,
      )
      await router.push({
         name: "OrgHome",
      })
      toast.success("Project deleted")
   })
}

const defaultNewHumanReadableName = ref("")

const onRenameProject = () => {
   $q.dialog({
      title: "Rename Project",
      message: "Enter the new name for the project",
      prompt: {
         model: defaultNewHumanReadableName.value,
         type: "text",
      },
      cancel: true,
   }).onOk(async (humanReadableName: string) => {
      await callWithLoading(
         projectStore.updateCurrentProject,
         $q,
         humanReadableName,
      )
      const org = await getOrg()
      if (!org) {
         throw new Error("Org is undefined")
      }
      router
         .push({
            name: "ProjectHome",
            params: { projectId: projectStore.projectId, orgSlug: org.slug },
         })
         .then(() => {
            toast.success("Project renamed")
         })
   })
}

const handleTabChange = (newTab: string) => {
   const { orgSlug, projectId } = route.params
   let routeName = "ProjectSettings"

   if (newTab === "install") {
      routeName = "ProjectSettingsInstall"
   } else if (newTab === "tours") {
      routeName = "ProjectSettingsTours"
   }

   router.push({
      name: routeName,
      params: { orgSlug, projectId },
   })
}

const showDisabledSubmit = computed(() => {
   return localProjectSettings.settings.holdbackPercentage === holdback.value
})

const { autoStartTour } = useTour(projectSettingsTour)

// Start the tour when the component is mounted and data is loaded
onMounted(async () => {
   await nextTick()
   if (localProjectSettings) {
      await autoStartTour()
   }
})
</script>
<style lang="scss" scoped>
#main-container {
   width: 100%;
   max-width: 600px;
}
h3 {
   margin: 0;
}
#project-settings-edit-form {
   padding: 1em;
   margin-bottom: 1em;
   width: 100%;
}
.q-field {
   margin-bottom: 10px;
}
#action-row {
   margin-top: 25px;
}
#holdback-slider {
   padding-top: 40px;
}
#optimization-metric-dropdown {
   width: 200px;
}
.action-button {
   margin-left: 10px;
}
#after-form {
   width: 100%;
   font-style: italic;
   text-align: left;
   a {
      color: $grey-9;
   }
}
#project-id {
   text-align: left;
   width: 100%;
   span {
      font-weight: bold;
   }
}
#page-container {
   min-height: 80vh;
}
</style>
